<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import ImovelAPI from 'dashboard/api/imoveis';
import { propertySearchQueryFromInput } from './propertySearchUtils';

const props = defineProps({
  modelValue: { type: Object, default: null },
  source: { type: String, default: '' },
  query: { type: String, default: '' },
  label: { type: String, default: 'Buscar imóvel' },
  helper: {
    type: String,
    default: 'Busque por código, endereço completo ou parte do endereço.',
  },
  placeholder: {
    type: String,
    default: 'Código, rua, bairro, cidade ou nome do imóvel',
  },
  required: { type: Boolean, default: false },
  includeEmpreendimentos: { type: Boolean, default: false },
  availableOnly: { type: Boolean, default: true },
  perSource: { type: Number, default: 8 },
  floatingResults: { type: Boolean, default: false },
  treatPropertiesAsPortfolio: { type: Boolean, default: false },
  mode: {
    type: String,
    default: 'property',
    validator: value => ['property', 'location'].includes(value),
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:source',
  'update:query',
  'select',
]);

const internalQuery = ref(props.query || '');
const results = ref([]);
const loading = ref(false);
const error = ref('');
const sourceWarnings = ref([]);
const isFocused = ref(false);
let searchSequence = 0;
let searchTimer = null;
let blurTimer = null;
const SOURCE_TIMEOUT_MS = 8000;

const valueFrom = (item, ...keys) => {
  if (!item) return '';
  const attrs = item.additional_attributes || item.additionalAttributes || {};
  const match = keys.find(
    key =>
      (item[key] != null && item[key] !== '') ||
      (attrs[key] != null && attrs[key] !== '')
  );
  if (!match) return '';
  return item[match] != null && item[match] !== '' ? item[match] : attrs[match];
};

const normalizeItems = data => data?.payload || data?.data || [];
const normalizePredictions = data => data?.predictions || data?.data || [];

const mediaFor = item => {
  const media = valueFrom(item, 'midias', 'media', 'images', 'photos') || [];
  const first = Array.isArray(media) ? media[0] : null;
  return typeof first === 'string' ? first : first?.url || first?.src || '';
};

const titleFor = item =>
  valueFrom(item, 'titulo', 'title', 'name', 'address') || 'Imóvel sem título';

const codeFor = item =>
  valueFrom(
    item,
    'codigo',
    'code',
    'property_code',
    'propertyCode',
    'external_id',
    'externalId',
    'house_id',
    'houseId',
    'sku'
  ) || valueFrom(item, 'id');

const locationFor = item =>
  [
    valueFrom(item, 'endereco', 'address', 'street'),
    valueFrom(item, 'bairro', 'neighborhood'),
    valueFrom(item, 'cidade', 'city'),
    valueFrom(item, 'uf', 'state'),
  ]
    .filter(Boolean)
    .join(', ');

const publicLinkFor = item =>
  valueFrom(
    item,
    'link_externo',
    'linkExterno',
    'external_url',
    'externalUrl',
    'public_url',
    'publicUrl',
    'contactWebsite',
    'url'
  );

const priceFor = item => {
  const attrs = item.additional_attributes || item.additionalAttributes || {};
  return (
    valueFrom(item, 'valor_venda', 'valorVenda', 'sale_price', 'salePrice') ||
    valueFrom(
      item,
      'valor_aluguel',
      'valorAluguel',
      'rent_price',
      'rentPrice'
    ) ||
    attrs.min_price ||
    attrs.minPrice ||
    attrs.price ||
    ''
  );
};

const formatBRL = value => {
  if (value == null || value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(numeric);
};

const sourceMeta = {
  portfolio: {
    label: 'DB interna',
    icon: 'i-lucide-database',
  },
  properties: {
    label: 'DB externa',
    icon: 'i-lucide-cloud',
  },
  empreendimentos: {
    label: 'Empreendimento',
    icon: 'i-lucide-building-2',
  },
};

const sourceMetaFor = source => {
  if (props.treatPropertiesAsPortfolio && source === 'properties') {
    return {
      label: 'Imóveis na casa',
      icon: 'i-lucide-house',
    };
  }

  return sourceMeta[source] || { label: source, icon: 'i-lucide-building' };
};

const normalizedResult = (source, item) => ({
  id: `${source}:${valueFrom(item, 'id', 'external_id', 'externalId')}`,
  source,
  item,
  title: titleFor(item),
  code: codeFor(item),
  location: locationFor(item),
  thumbnail: mediaFor(item),
  link: publicLinkFor(item),
  price: priceFor(item),
  sourceLabel: sourceMetaFor(source).label,
  sourceIcon: sourceMetaFor(source).icon,
});

const locationPartsFrom = prediction => {
  const fullText =
    prediction.fullText ||
    prediction.full_text ||
    prediction.description ||
    prediction.primaryText ||
    '';
  const secondary = prediction.secondaryText || prediction.secondary_text || '';
  const parts = String(secondary || fullText)
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  const cityState = parts[parts.length - 1] || '';
  const [city, state] = cityState.split(/\s+-\s+|\s+/).filter(Boolean);

  return {
    neighborhood: parts.length > 1 ? parts[0] : '',
    city: city || '',
    state: state || '',
    fullText,
  };
};

const normalizedLocationResult = prediction => {
  const parts = locationPartsFrom(prediction);

  return {
    id: `location:${prediction.placeId || prediction.place_id || parts.fullText}`,
    source: 'location',
    item: {
      placeId: prediction.placeId || prediction.place_id || '',
      primaryText: prediction.primaryText || prediction.primary_text || '',
      secondaryText:
        prediction.secondaryText || prediction.secondary_text || '',
      fullText: parts.fullText,
      bairro: parts.neighborhood,
      cidade: parts.city,
      uf: parts.state,
    },
    title: prediction.primaryText || prediction.primary_text || parts.fullText,
    code: '',
    location:
      prediction.secondaryText || prediction.secondary_text || parts.fullText,
    thumbnail: '',
    link: '',
    price: '',
    sourceLabel: 'Localização',
    sourceIcon: 'i-lucide-map-pin',
  };
};

const selectedKey = computed(() => {
  if (!props.modelValue) return '';
  const source = props.source || 'portfolio';
  return `${source}:${valueFrom(props.modelValue, 'id', 'external_id', 'externalId')}`;
});
const showResultsPanel = computed(
  () => !props.floatingResults || isFocused.value
);
const sourceWarningMessage = computed(() => {
  if (!sourceWarnings.value.length) return '';

  const unavailable = sourceWarnings.value.join(', ');
  return `Fonte indisponível: ${unavailable}. Exibindo resultados parciais quando disponíveis.`;
});

const withSourceTimeout = request =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Tempo limite ao buscar imóveis.')),
      SOURCE_TIMEOUT_MS
    );
    request.then(
      response => {
        clearTimeout(timer);
        resolve(response);
      },
      errorResponse => {
        clearTimeout(timer);
        reject(errorResponse);
      }
    );
  });

const searchSources = async () => {
  searchSequence += 1;
  const requestId = searchSequence;
  loading.value = true;
  error.value = '';
  sourceWarnings.value = [];
  results.value = [];

  const query = internalQuery.value.trim();
  if (query.length < 2) {
    loading.value = false;
    results.value = [];
    sourceWarnings.value = [];
    return;
  }

  if (props.mode === 'location') {
    try {
      const { data } = await ImovelAPI.autocompleteAddress({
        query,
      });
      if (requestId !== searchSequence) return;
      results.value = normalizePredictions(data).map(normalizedLocationResult);
    } catch {
      error.value = 'Não foi possível buscar localizações agora.';
      results.value = [];
      sourceWarnings.value = [];
    } finally {
      if (requestId === searchSequence) loading.value = false;
    }
    return;
  }

  const params = {
    q: propertySearchQueryFromInput(query),
    page: 1,
    per_page: props.perSource,
    apenas_disponiveis: props.availableOnly ? true : '',
  };
  const sources = [
    ['portfolio', ImovelAPI.get(params)],
    ['properties', ImovelAPI.externalProperties(params)],
  ];

  if (props.includeEmpreendimentos) {
    sources.push(['empreendimentos', ImovelAPI.empreendimentos(params)]);
  }

  const nextResults = [];
  let completedSources = 0;
  let failedSources = 0;

  await Promise.all(
    sources.map(([source, request]) =>
      withSourceTimeout(request)
        .then(response => {
          if (requestId !== searchSequence) return;
          normalizeItems(response.data).forEach(item => {
            nextResults.push(normalizedResult(source, item));
          });
          results.value = [...nextResults];
        })
        .catch(() => {
          failedSources += 1;
          if (requestId === searchSequence) {
            sourceWarnings.value = [
              ...sourceWarnings.value,
              sourceMetaFor(source).label,
            ];
          }
        })
        .finally(() => {
          completedSources += 1;
          if (requestId !== searchSequence) return;
          if (completedSources === sources.length) {
            if (!nextResults.length && failedSources === sources.length) {
              error.value = 'Não foi possível buscar imóveis agora.';
              sourceWarnings.value = [];
            }
            loading.value = false;
          }
        })
    )
  );
};

const scheduleSearch = () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(searchSources, 250);
};

const selectResult = result => {
  emit('update:modelValue', result.item);
  emit('update:source', result.source);
  emit('select', result);
};

const openPublicLink = result => {
  if (!result.link || typeof window === 'undefined') return;
  window.open(result.link, '_blank', 'noopener');
};

const handleFocusIn = () => {
  if (blurTimer) clearTimeout(blurTimer);
  isFocused.value = true;
};

const handleFocusOut = () => {
  if (!props.floatingResults) return;
  blurTimer = setTimeout(() => {
    isFocused.value = false;
  }, 160);
};

watch(
  () => props.query,
  value => {
    if (value !== internalQuery.value) {
      internalQuery.value = value || '';
    }
  }
);

watch(internalQuery, value => {
  emit('update:query', value);
  scheduleSearch();
});

onMounted(searchSources);
onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer);
  if (blurTimer) clearTimeout(blurTimer);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    class="relative grid gap-3"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
  >
    <label class="flex flex-col gap-1.5 text-[13px] font-redHatText">
      <span class="font-semibold text-lais-text-body leading-tight">
        {{ label }}
        <span v-if="required" class="text-lais-link">*</span>
      </span>
      <div class="flex min-w-0 gap-2">
        <input
          v-model="internalQuery"
          type="search"
          :placeholder="placeholder"
          class="h-10 min-w-0 flex-1 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary-50"
          @keydown.enter.prevent="searchSources"
        />
        <button
          type="button"
          class="grid h-10 w-10 shrink-0 place-items-center rounded-lais border border-lais-border bg-white text-lais-text transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
          :disabled="loading"
          title="Buscar imóveis"
          @click="searchSources"
        >
          <span
            :class="
              loading
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-search'
            "
            class="size-4"
            aria-hidden="true"
          />
        </button>
      </div>
      <span v-if="error" class="text-[12px] text-lais-danger leading-tight">
        {{ error }}
      </span>
      <span
        v-else-if="helper"
        class="text-[12px] text-lais-text-muted leading-tight"
      >
        {{ helper }}
      </span>
      <span
        v-if="sourceWarningMessage"
        class="text-[12px] font-semibold text-amber-700 leading-tight"
      >
        {{ sourceWarningMessage }}
      </span>
    </label>

    <div
      v-if="showResultsPanel"
      class="grid max-h-[340px] gap-2 overflow-y-auto pr-1"
      :class="
        floatingResults
          ? 'absolute left-0 right-0 top-full z-30 mt-2 rounded-lais border border-lais-border bg-white p-2 shadow-xl'
          : ''
      "
    >
      <article
        v-for="result in results"
        :key="result.id"
        role="button"
        tabindex="0"
        class="flex min-w-0 cursor-pointer items-start gap-3 rounded-lais border p-3 text-left transition-colors"
        :class="
          selectedKey === result.id
            ? 'border-lais-primary bg-lais-primary-50'
            : 'border-lais-border bg-white hover:border-lais-primary'
        "
        @click="selectResult(result)"
        @keydown.enter.prevent="selectResult(result)"
      >
        <div
          class="grid size-12 shrink-0 place-items-center overflow-hidden rounded-lais-sm bg-lais-bg-soft"
        >
          <img
            v-if="result.thumbnail"
            :src="result.thumbnail"
            :alt="result.title"
            class="h-full w-full object-cover"
          />
          <span
            v-else
            class="i-lucide-building-2 size-5 text-lais-text-muted"
            aria-hidden="true"
          />
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex min-w-0 flex-wrap items-center gap-2">
            <span
              class="inline-flex items-center gap-1 rounded-full bg-lais-bg-soft px-2 py-0.5 text-[11px] font-semibold text-lais-text-body"
            >
              <span
                class="size-3"
                :class="[result.sourceIcon]"
                aria-hidden="true"
              />
              {{ result.sourceLabel }}
            </span>
            <span
              v-if="result.code"
              class="rounded-full bg-lais-primary-50 px-2 py-0.5 text-[11px] font-semibold text-lais-primary"
            >
              Código {{ result.code }}
            </span>
          </div>
          <p class="mt-1 break-words font-semibold text-lais-text">
            {{ result.title }}
          </p>
          <p class="text-[12px] text-lais-text-body">
            {{ result.location || 'Localização não informada' }}
          </p>
          <p
            v-if="result.price"
            class="mt-1 text-[12px] font-semibold text-lais-primary"
          >
            {{ formatBRL(result.price) }}
          </p>
        </div>
        <button
          v-if="result.link"
          type="button"
          class="grid size-8 shrink-0 place-items-center rounded-full border border-lais-border bg-white text-lais-primary transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
          title="Abrir link do imóvel"
          @click.stop="openPublicLink(result)"
        >
          <span class="i-lucide-globe-2 size-4" aria-hidden="true" />
        </button>
        <span
          v-if="selectedKey === result.id"
          class="i-lucide-check-circle-2 size-5 shrink-0 text-lais-primary"
          aria-hidden="true"
        />
      </article>

      <div
        v-if="loading && !results.length"
        class="rounded-lais border border-lais-border p-4 text-center text-[13px] text-lais-text-muted"
      >
        Buscando imóveis…
      </div>
      <div
        v-else-if="!loading && !results.length && !error"
        class="rounded-lais border border-lais-border p-4 text-center text-[13px] text-lais-text-muted"
      >
        Nenhum imóvel encontrado para esta busca.
      </div>
    </div>
  </div>
</template>
